/**
 * Sanitize notice body HTML to the v1 allowlist.
 * Prefer DOMPurify in the browser; fall back to DOMParser / regex.
 */

import DOMPurify from "dompurify";
import {
  NOTICE_BODY_ALLOWED_ATTR,
  NOTICE_BODY_ALLOWED_TAGS,
  isPlainNoticeBody,
  normalizeNoticeBodyHtml,
} from "./noticeBodyHtml";

const ALLOWED = new Set<string>(NOTICE_BODY_ALLOWED_TAGS);

function stripWithRegex(html: string): string {
  let out = html
    .replace(
      /<\s*(script|style|iframe|object|embed)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi,
      "",
    )
    .replace(/ on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/<\/?(?!\/?(?:p|br|strong|b|em|i|u|ul|ol|li)\b)[a-z][^>]*>/gi, "");

  out = out.replace(/<\s*([a-z]+)(\s[^>]*)?>/gi, (_m, tag: string) => {
    const t = tag.toLowerCase();
    if (!ALLOWED.has(t)) return "";
    if (t === "br") return "<br>";
    return `<${t}>`;
  });

  return normalizeNoticeBodyHtml(out);
}

function stripWithDomParser(html: string): string {
  if (typeof DOMParser === "undefined") {
    return stripWithRegex(html);
  }

  const doc = new DOMParser().parseFromString(
    `<div id="nb-root">${html}</div>`,
    "text/html",
  );
  const root = doc.getElementById("nb-root");
  if (!root) return "";

  const walk = (node: Node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        const tag = el.tagName.toLowerCase();
        if (!ALLOWED.has(tag)) {
          while (el.firstChild) {
            node.insertBefore(el.firstChild, el);
          }
          node.removeChild(el);
          continue;
        }
        while (el.attributes.length > 0) {
          el.removeAttribute(el.attributes[0].name);
        }
        walk(el);
      } else if (child.nodeType === Node.COMMENT_NODE) {
        node.removeChild(child);
      }
    }
  };

  walk(root);
  return normalizeNoticeBodyHtml(root.innerHTML);
}

/** Sync sanitize for submit handlers / render paths. */
export function sanitizeNoticeBodyHtml(dirty: string): string {
  if (!dirty) return "";
  if (isPlainNoticeBody(dirty)) {
    return dirty;
  }

  try {
    if (typeof window !== "undefined" && DOMPurify?.sanitize) {
      const clean = DOMPurify.sanitize(dirty, {
        ALLOWED_TAGS: [...NOTICE_BODY_ALLOWED_TAGS],
        ALLOWED_ATTR: [...NOTICE_BODY_ALLOWED_ATTR],
      });
      return normalizeNoticeBodyHtml(clean);
    }
  } catch {
    // fall through
  }

  return stripWithDomParser(dirty);
}

export function warmupNoticeBodySanitizer(): void {
  // DOMPurify is imported eagerly for the dashboard bundle.
}
